import { useEffect, useState, type ReactNode } from 'react';
import { useApp, useFetch, type DrawerState } from '../store/app';
import { Icon } from '../components/Icon';
import { Button, StatusBadge } from '../components/primitives';
import { Field, type FieldDef } from '../components/Field';
import { FORMS, FORM_DEFAULTS } from './forms';
import { api, uploadInvoicePdf } from '../lib/api';
import { st } from '../lib/status';
import { can, type Resource } from '../lib/rbac';
import type { Client, Contract, DataPoint, Invoice, InvoiceItem } from '../../shared/types';

// Maps a form drawer type to the RBAC resource its writes touch.
const DRAWER_RESOURCE: Record<string, Resource> = { person: 'people', client: 'clients', project: 'projects', contact: 'contacts', profile: 'profile' };

export function DrawerHost() {
  const { drawer, closeDrawer } = useApp();
  if (!drawer) return null;
  const wide = drawer.type === 'invoice-view' || drawer.type === 'invoice-new';
  return (
    <DrawerShell wide={wide} onClose={closeDrawer}>
      <DrawerBody key={`${drawer.type}-${drawer.id ?? 'new'}`} drawer={drawer} />
    </DrawerShell>
  );
}

function DrawerShell({ children, wide, onClose }: { children: ReactNode; wide: boolean; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,.42)', display: 'flex', justifyContent: 'flex-end', zIndex: 50, animation: 'ccover .15s ease' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: wide ? 672 : 452, maxWidth: '94vw', height: '100%', background: 'var(--surface)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', animation: 'ccslide .2s ease' }}>
        {children}
      </div>
    </div>
  );
}

function Header({ title, sub, badge, onClose }: { title: string; sub?: string; badge?: ReturnType<typeof st>; onClose: () => void }) {
  return (
    <div style={{ flex: '0 0 auto', padding: '16px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</div>
          {badge && <StatusBadge badge={badge} />}
        </div>
        {sub && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
      </div>
      <button onClick={onClose} className="cc-hover-s2" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--text-2)', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="close" size={20} />
      </button>
    </div>
  );
}

function DrawerBody({ drawer }: { drawer: DrawerState }) {
  if (drawer.type === 'invoice-view') return <InvoiceView id={drawer.id!} />;
  if (drawer.type === 'invoice-new') return <InvoiceNew />;
  return <FormDrawer drawer={drawer} />;
}

// ---- Generic form drawer (person/client/project/contact/profile) ----
function FormDrawer({ drawer }: { drawer: DrawerState }) {
  const { closeDrawer, refresh, user } = useApp();
  const cfg = FORMS[drawer.type]!;
  const edit = drawer.mode === 'edit';
  const recordPath = edit && drawer.id ? `${cfg.endpoint}/${drawer.id}` : drawer.type === 'profile' ? '/profile' : null;
  const { data: record } = useFetch<Record<string, unknown>>(recordPath);
  const editingProfile = drawer.type === 'profile';

  // Whether the current user may persist this form — mirrors the server guard so
  // we don't show a Save/Delete that will only 403 (e.g. a drawer opened via search).
  const resource = DRAWER_RESOURCE[drawer.type];
  const canSave = !user || !resource ? true : can(user.role, edit ? 'update' : 'create', resource);
  const canDelete = !!cfg.delLabel && !!user && !!resource && can(user.role, 'delete', resource);

  // Options sources: only fetch what this form's fields actually need (avoids
  // hitting /datapoints or /clients — and 403-ing — when no field references them).
  const needsDataPoints = cfg.fields.some((f) => f.optionsSource && f.optionsSource !== 'clients' && f.optionsSource !== 'contracts');
  const needsClients = cfg.fields.some((f) => f.optionsSource === 'clients');
  const needsContracts = cfg.fields.some((f) => f.optionsSource === 'contracts');
  const { data: dataPoints } = useFetch<DataPoint[]>(needsDataPoints ? '/datapoints' : null);
  const { data: clients } = useFetch<Client[]>(needsClients ? '/clients' : null);
  const { data: contractList } = useFetch<Contract[]>(needsContracts ? '/contracts' : null);
  const contractLabel = (k: Contract) => `${k.name}${k.reference ? ` · ${k.reference}` : ''}${k.client_name ? ` (${k.client_name})` : ''}`;

  const [vals, setVals] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingProfile && record) setVals({ ...record });
    else if (edit && record) setVals({ ...record });
    else setVals({ ...(FORM_DEFAULTS[drawer.type] || {}), ...(drawer.context || {}) });
  }, [record, edit, drawer.type]);

  const resolveOptions = (src?: string): string[] | undefined => {
    if (!src) return undefined;
    if (src === 'clients') return (clients || []).map((c) => c.name);
    if (src === 'contracts') return ['None', ...(contractList || []).map(contractLabel)];
    const grp = (dataPoints || []).filter((d) => d.group === src).map((d) => d.value);
    return grp.length ? grp : undefined;
  };

  // For id-backed selects we display names but store ids.
  const toName = (key: string, v: unknown): string => {
    if (key === 'client_id' && clients) return clients.find((c) => c.id === Number(v))?.name || '';
    if (key === 'contract_id') return v ? (contractList || []).filter((k) => k.id === Number(v)).map(contractLabel)[0] || '' : 'None';
    if (key === 'primary') return v ? 'Yes' : 'No';
    return v == null ? '' : String(v);
  };
  const fromName = (key: string, v: string): unknown => {
    if (key === 'client_id' && clients) return clients.find((c) => c.name === v)?.id ?? null;
    if (key === 'contract_id') return v === 'None' ? null : (contractList || []).find((k) => contractLabel(k) === v)?.id ?? null;
    if (key === 'primary') return v === 'Yes';
    return v;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of cfg.fields) {
        const raw = vals[f.key];
        payload[f.key] = f.kind === 'chips' ? raw || [] : f.kind === 'segment' && f.key === 'primary' ? raw === 'Yes' || raw === true : raw;
      }
      if (editingProfile) await api.put('/profile', payload);
      else if (edit) await api.put(`${cfg.endpoint}/${drawer.id}`, payload);
      else await api.post(cfg.endpoint, payload);
      refresh();
      closeDrawer();
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!edit || !drawer.id) return;
    if (!confirm(`${cfg.delLabel}? This cannot be undone.`)) return;
    await api.del(`${cfg.endpoint}/${drawer.id}`);
    refresh();
    closeDrawer();
  };

  return (
    <>
      <Header title={cfg.title(edit)} sub={cfg.sub(edit)} onClose={closeDrawer} />
      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {cfg.fields.map((f) => {
            const def: FieldDef = { ...f, options: f.optionsSource ? resolveOptions(f.optionsSource) : f.options };
            const v = f.kind === 'chips' ? (vals[f.key] as string[]) || [] : toName(f.key, vals[f.key]);
            return (
              <Field
                key={f.key}
                def={def}
                value={v}
                onChange={(nv) => setVals((s) => ({ ...s, [f.key]: f.kind === 'chips' ? nv : fromName(f.key, nv as string) }))}
              />
            );
          })}
        </div>
      </div>
      <Footer>
        {edit && canDelete ? <Button variant="danger" onClick={del}>{cfg.delLabel}</Button> : <span />}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={closeDrawer}>{canSave ? 'Cancel' : 'Close'}</Button>
          {canSave && <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : edit ? 'Save changes' : 'Create'}</Button>}
        </div>
      </Footer>
    </>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return <div style={{ flex: '0 0 auto', padding: '14px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>{children}</div>;
}

const metaCell = (label: string, value: ReactNode) => (
  <div key={label}>
    <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 500 }}>{value}</div>
  </div>
);

// ---- Invoice view (review/approve) ----
function InvoiceView({ id }: { id: number }) {
  const { closeDrawer, refresh, user } = useApp();
  const { data: inv } = useFetch<Invoice>(`/invoices/${id}`);
  const [busy, setBusy] = useState(false);
  if (!inv) return <Header title="Invoice" onClose={closeDrawer} />;
  const canApprove = user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager');
  const canDelete = !!user && can(user.role, 'delete', 'invoices');
  const showFooter = canDelete || (canApprove && inv.status === 'pending');

  const act = async (status: string) => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}`, { status });
      refresh();
      closeDrawer();
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!confirm(`Delete invoice ${inv.num}? This permanently removes it and any attached PDF.`)) return;
    setBusy(true);
    try {
      await api.del(`/invoices/${id}`);
      refresh();
      closeDrawer();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Could not delete invoice.');
      setBusy(false);
    }
  };

  return (
    <>
      <Header title={`Invoice ${inv.num}`} sub={`Submitted by ${inv.person_name || 'Unassigned'} · ${inv.date}`} badge={st(inv.status)} onClose={closeDrawer} />
      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          {metaCell('From', inv.person_name || 'Unassigned')}
          {metaCell('Project', inv.project_name)}
          {metaCell('Billing period', inv.billing_period)}
          {metaCell('Date', inv.date)}
        </div>
        <ItemsTable items={inv.items || []} />
        <Totals subtotal={inv.subtotal} tax={inv.tax} total={inv.total} />
        {inv.pdf_r2_key && (
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
            <Icon name="picture_as_pdf" size={22} color="var(--danger)" />
            <a href={`/api/invoices/${id}/pdf`} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}>{inv.num}.pdf</a>
            <div style={{ flex: 1 }} />
            <Icon name="download" size={18} color="var(--text-3)" />
          </div>
        )}
        {inv.notes && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Notes</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{inv.notes}</div>
          </div>
        )}
      </div>
      {showFooter && (
        <Footer>
          {canDelete ? <Button variant="danger" onClick={del} disabled={busy}>Delete</Button> : <span />}
          {canApprove && inv.status === 'pending' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={() => act('overdue')} disabled={busy}>Reject</Button>
              <Button onClick={() => act('approved')} disabled={busy}>Approve</Button>
            </div>
          )}
        </Footer>
      )}
    </>
  );
}

function ItemsTable({ items }: { items: InvoiceItem[] }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 90px', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>
        <div>Description</div><div>Qty</div><div>Rate</div><div>Amount</div>
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 90px', gap: 10, padding: '10px 12px', borderTop: i ? '1px solid var(--border)' : 0, fontSize: 13 }}>
          <div>{it.description}</div>
          <div className="cc-mono">{it.qty}</div>
          <div className="cc-mono">{it.rate}</div>
          <div className="cc-mono">{it.amount}</div>
        </div>
      ))}
    </div>
  );
}

function Totals({ subtotal, tax, total }: { subtotal: string; tax: string; total: string }) {
  return (
    <div style={{ marginTop: 14, marginLeft: 'auto', width: 240 }}>
      {[['Subtotal', subtotal], ['Tax', tax]].map(([l, v]) => (
        <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-2)', padding: '4px 0' }}>
          <span>{l}</span><span className="cc-mono">{v}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, padding: '8px 0 0', borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <span>Total</span><span className="cc-mono">{total}</span>
      </div>
    </div>
  );
}

// ---- Invoice new (submit) ----
function InvoiceNew() {
  const { closeDrawer, refresh } = useApp();
  const { data: projects } = useFetch<{ id: number; name: string }[]>('/projects');
  const [project, setProject] = useState('');
  const [period, setPeriod] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<InvoiceItem[]>([{ description: '', qty: '1', rate: '$0.00', amount: '$0.00' }]);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const num = (s: string) => Number(String(s).replace(/[^0-9.\-]/g, '')) || 0;
  const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const computed = items.map((it) => ({ ...it, amount: money(num(it.qty) * num(it.rate)) }));
  const subtotal = computed.reduce((s, it) => s + num(it.amount), 0);

  const update = (i: number, key: keyof InvoiceItem, v: string) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [key]: v } : it)));

  const submit = async (status: 'draft' | 'pending') => {
    setBusy(true);
    try {
      const created = await api.post<Invoice>('/invoices', {
        project_name: project,
        billing_period: period,
        notes,
        status,
        items: computed,
        subtotal: money(subtotal),
        tax: '$0.00',
        total: money(subtotal),
        amount: money(subtotal),
      });
      if (file && created?.id) await uploadInvoicePdf(created.id, file);
      refresh();
      closeDrawer();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header title="Submit an invoice" sub="Bill CCSilo for completed work" onClose={closeDrawer} />
      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <Field def={{ kind: 'select', key: 'project', label: 'Project', placeholder: 'Select project', options: (projects || []).map((p) => p.name) }} value={project} onChange={(v) => setProject(v as string)} />
          <Field def={{ kind: 'text', key: 'period', label: 'Billing period', placeholder: '1–28 Jun 2026' }} value={period} onChange={(v) => setPeriod(v as string)} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Line items</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {computed.map((it, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 54px 84px 84px 28px', gap: 6, alignItems: 'center' }}>
              <input value={it.description} placeholder="Description" onChange={(e) => update(i, 'description', e.target.value)} style={miniInput} />
              <input value={it.qty} onChange={(e) => update(i, 'qty', e.target.value)} style={miniInput} />
              <input value={it.rate} onChange={(e) => update(i, 'rate', e.target.value)} style={miniInput} />
              <div className="cc-mono" style={{ fontSize: 12.5, textAlign: 'right' }}>{it.amount}</div>
              <span onClick={() => setItems((arr) => arr.filter((_, idx) => idx !== i))} style={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="close" size={16} color="var(--text-3)" /></span>
            </div>
          ))}
        </div>
        <button onClick={() => setItems((a) => [...a, { description: '', qty: '1', rate: '$0.00', amount: '$0.00' }])} style={{ appearance: 'none', border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'inherit', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon name="add" size={17} />Add line item
        </button>
        <Totals subtotal={money(subtotal)} tax="$0.00" total={money(subtotal)} />
        <label style={{ marginTop: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, border: '1.5px dashed var(--border-2)', borderRadius: 10, padding: 20, cursor: 'pointer', color: 'var(--text-3)' }}>
          <Icon name="upload_file" size={26} />
          <span style={{ fontSize: 13 }}>{file ? file.name : 'Drop a PDF or browse'}</span>
          <input type="file" accept="application/pdf" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div style={{ marginTop: 16 }}>
          <Field def={{ kind: 'textarea', key: 'notes', label: 'Notes', placeholder: 'Net terms, bank details…', full: true }} value={notes} onChange={(v) => setNotes(v as string)} />
        </div>
      </div>
      <Footer>
        <Button variant="secondary" onClick={() => submit('draft')} disabled={busy}>Save draft</Button>
        <Button icon="send" onClick={() => submit('pending')} disabled={busy}>Submit invoice</Button>
      </Footer>
    </>
  );
}

const miniInput = { border: '1px solid var(--border-2)', background: 'var(--surface)', borderRadius: 6, padding: '7px 9px', fontFamily: 'inherit', fontSize: 12.5, color: 'var(--text)', outline: 'none', width: '100%' } as const;
